import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers } from "./shared";
import { Patient } from "../patient.model";

interface MusculoskeletalFields {
  // Fall Risk Assessment Tool

  /**
   * The patient's age range.
   *
   * Options:
   * "60 - 69 years (1 point)",
   * "70 -79 years (2 points)",
   * or "greater than or equal to 80 years (3 points)".
   */
  fallRiskAge: string;

  /**
   * Whether the patient has had "one fall within 6 months before admission" (true)
   * or not (false).
   */
  fallHistory: boolean;

  /**
   * AKA "elimination, bowel and urine."
   *
   * Options:
   * "Incontinence (2 points)",
   * "Urgency or frequency (2 points)",
   * or "Urgency/frequency and incontinence (4 points)".
   */
  fallRiskEbu: string;

  /**
   * The number of high fall risk drugs the patient is currently on.
   *
   * Options:
   * "On 1 high fall risk drug (3 points)",
   * "On 2 or more high fall risk drugs (5 points)",
   * or "Sedated procedure within past 24 hours (7 points)".
   */
  fallRiskMedications: string;

  /**
   * AKA "patient care equipment." The number of pieces of equipment that tethers
   * the patient.
   *
   * Options:
   * "One present (1 point)",
   * "Two present (2 points)",
   * or "3 or more present (3 points)".
   */
  fallRiskPce: string; // AKA patient care equipment

  /**
   * The patient's fall risk score based on their mobility.
   *
   * Options:
   * "Requires assistance or supervision for mobility, transfer, or ambulation (2 points)",
   * "Unsteady gait (2 points)", and
   * "Visual or auditory impairment affecting mobility (2 points)".
   */
  fallRiskMobility: string[];

  /**
   * The patient's fall risk score based on their cognitive ability.
   *
   * Options:
   * "Altered awareness of immediate physical environment (1 point)",
   * "Impulsive (2 points)", and
   * "Lack of understanding of one's physical and cognitive limitations (4 points)".
   */
  fallRiskCognition: string[];

  /**
   * The patient's fall score based on the scoring provided by the above options.
   */
  fallScore: number;



  // Humpty Dumpty Fall Scale (Pediatrics only)

  /*
   * The patient's age.
   * Options: 3 years or younger (4 points), 3 to 7 years (3 points), or 7 to 13 years (2 points), 13 years or older (1 point).
   */
  HDage: string;

  /*
   * The patient's gender.
   * Options: male (2 points), female (1 point).
   */
  HDgender: string;

  /*
   * The patient's diagnosis.
   * Options:
   * - Neurological Diagnosis (4 points)
   * - Alterations in Oxygenation (Respiratory Diagnosis, Dehydration, Anemia, Anorexia, Syncope/Dizziness, etc.) (3 points)
   * - Psyc/Behavioral Disorders (2 points)
   * - Other Diagnosis (1 point)
   */
  HDdiagnosis: string;

  /*
   * The patient's cognitive impairments.
   * Options: Not Aware of Limitations (3 points), Forgets Limitations (2 points), Oriented to own ability (1 point).
   */
  HDcognitiveImpairment: string;

  /*
   * The patient's environmental factors.
   * Options:
   * - History of Falls or Infant-Toddler Placed in Bed (4 points)
   * - Patient uses assistive devices or Infant-Toddler in Crib or Furniture/Lighting (3 points)
   * - Patient Placed in Bed (2 points)
   * - Outpatient Area (1 point)
   */
  HDenvironmentalFactors: string;

  /*
   * The patient's response to surgery or surgical drugs.
   * Options: Within 24 hours (3 points), Within 48 hours (2 point), More than 48 hours/None (1 point).
   */
  HDresponseToSurgery: string;

  /*
   * The patient's medication usage.
   * Options: 
   * - Multiple usage of: Sedatives (excluding ICU patients sedated and paralyzed), Hypnotics, Barbiturates, Phenothiazines (3 points)
   */
  HDmedicationUsage: string;

  /*
   * The patient's Humpty Dumpty fall risk score based on the preceeding factors.
   * Options: male (2 points), female (1 point).
   */
  HDtotalScore: number;



  /**
   * The mobility/transfers the patient has.
   *
   * Options: standby assist, one assist, two assists, or max assist.
   */
  mobilityAndTransfers: string;

  /**
   * The assistive devices a patient uses for mobility.
   *
   * Options: gait belt, walker, cane, and trapeze.
   */
  assistiveDevices: string[];

  limitedRangeOfMotion: string;
  deformitiesPresent: string;
  otherFindings: string;
}
export type MusculoskeletalFormGroup = FormGroup<ControlsOf<MusculoskeletalFields>>;

export const MusculoskeletalAssessmentHelpers: AssessmentHelpers<MusculoskeletalAssessment, Patient> = {
  createFormGroup: createMusculoskeletalFormGroup,
  toPDF: musculoskeletalAssessmentToPDF,
  formToObject: formToMusculoskeletalAssessment,
  initialState: {
    // Refer to the "Johns Hopkins Fall Risk Assessment Tool"
    fallRiskAge: '',
    fallHistory: false,
    // ebu = elimination, bowel and urine
    fallRiskEbu: '',
    fallRiskMedications: '',
    // pce = patient care equipment
    fallRiskPce: '', // one present, two present, or 3 or more present
    fallRiskMobility: [],
    fallRiskCognition: [],
    fallScore: 0,
    // Humpty Dumpty Fall Scale (Pediatrics only)
    HDage: '',
    HDgender: '',
    HDdiagnosis: '',
    HDcognitiveImpairment: '',
    HDenvironmentalFactors: '',
    HDresponseToSurgery: '',
    HDmedicationUsage: '',
    HDtotalScore: 0,
    mobilityAndTransfers: '',
    assistiveDevices: [],
    limitedRangeOfMotion: '',
    deformitiesPresent: '',
    otherFindings: '',
  },
};

/**
 * Holds information about the patient's musculoskeletal assessment.
 */
export class MusculoskeletalAssessment {
  fallRiskAge: string;
  fallHistory: boolean;
  fallRiskEbu: string;
  fallRiskMedications: string;
  fallRiskPce: string; // AKA patient care equipment
  fallRiskMobility: string[];
  fallRiskCognition: string[];
  fallScore: number;
  HDage: string;
  HDgender: string;
  HDdiagnosis: string;
  HDcognitiveImpairment: string;
  HDenvironmentalFactors: string;
  HDresponseToSurgery: string;
  HDmedicationUsage: string;
  HDtotalScore: number;
  mobilityAndTransfers: string;
  assistiveDevices: string[];
  limitedRangeOfMotion: string;
  deformitiesPresent: string;
  otherFindings: string;

  constructor(data: Partial<MusculoskeletalFields>) {
    const is = MusculoskeletalAssessmentHelpers.initialState;
    this.fallRiskAge = data.fallRiskAge || is['fallRiskAge'];
    this.fallHistory = data.fallHistory ?? is['fallHistory'];
    this.fallRiskEbu = data.fallRiskEbu || is['fallRiskEbu'];
    this.fallRiskMedications = data.fallRiskMedications || is['fallRiskMedications'];
    this.fallRiskPce = data.fallRiskPce || is['fallRiskPce'];
    this.fallRiskMobility = data.fallRiskMobility || is['fallRiskMobility'];
    this.fallRiskCognition = data.fallRiskCognition || is['fallRiskCognition'];
    this.fallScore = data.fallScore ?? is['fallScore'];
    this.HDage = data.HDage || is['HDage'];
    this.HDgender = data.HDgender || is['HDgender'];
    this.HDdiagnosis = data.HDdiagnosis || is['HDdiagnosis'];
    this.HDcognitiveImpairment = data.HDcognitiveImpairment || is['HDcognitiveImpairment'];
    this.HDenvironmentalFactors = data.HDenvironmentalFactors || is['HDenvironmentalFactors'];
    this.HDresponseToSurgery = data.HDresponseToSurgery || is['HDresponseToSurgery'];
    this.HDmedicationUsage = data.HDmedicationUsage || is['HDmedicationUsage'];
    this.HDtotalScore = data.HDtotalScore ?? is['HDtotalScore'];
    this.mobilityAndTransfers = data.mobilityAndTransfers || is['mobilityAndTransfers'];
    this.assistiveDevices = data.assistiveDevices || is['assistiveDevices'];
    this.limitedRangeOfMotion = data.limitedRangeOfMotion || is['limitedRangeOfMotion'];
    this.deformitiesPresent = data.deformitiesPresent || is['deformitiesPresent'];
    this.otherFindings = data.otherFindings || is['otherFindings'];
  }
}


function createMusculoskeletalFormGroup(musculoskeletalAssessment?: MusculoskeletalAssessment) {
  const data = musculoskeletalAssessment || new MusculoskeletalAssessment(MusculoskeletalAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<MusculoskeletalFields>>({
    fallRiskAge: new FormControl<string | null>(data.fallRiskAge),
    fallHistory: new FormControl<boolean | null>(data.fallHistory),
    fallRiskEbu: new FormControl<string | null>(data.fallRiskEbu),
    fallRiskMedications: new FormControl<string | null>(data.fallRiskMedications),
    fallRiskPce: new FormControl<string | null>(data.fallRiskPce),
    fallRiskMobility: new FormControl<string[] | null>(data.fallRiskMobility),
    fallRiskCognition: new FormControl<string[] | null>(data.fallRiskCognition),
    fallScore: new FormControl<number | null>(data.fallScore),
    HDage: new FormControl<string | null>(data.HDage),
    HDgender: new FormControl<string | null>(data.HDgender),
    HDdiagnosis: new FormControl<string | null>(data.HDdiagnosis),
    HDcognitiveImpairment: new FormControl<string | null>(data.HDcognitiveImpairment),
    HDenvironmentalFactors: new FormControl<string | null>(data.HDenvironmentalFactors),
    HDresponseToSurgery: new FormControl<string | null>(data.HDresponseToSurgery),
    HDmedicationUsage: new FormControl<string | null>(data.HDmedicationUsage),
    HDtotalScore: new FormControl<number | null>(data.HDtotalScore),
    mobilityAndTransfers: new FormControl<string | null>(data.mobilityAndTransfers),
    assistiveDevices: new FormControl<string[] | null>(data.assistiveDevices),
    limitedRangeOfMotion: new FormControl<string | null>(data.limitedRangeOfMotion),
    deformitiesPresent: new FormControl<string | null>(data.deformitiesPresent),
    otherFindings: new FormControl<string | null>(data.otherFindings),
  });
}

function musculoskeletalAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: MusculoskeletalAssessment, patient: Patient
): void {
  pdf.addSectionTitle("Musculoskeletal");
  pdf.addTwoColumnText("Mobility/Transfers", assessment.mobilityAndTransfers,
    "Assistive Devices", assessment.assistiveDevices.join(", "));
  pdf.currY += 10;
  if (patient.isPediatric()) {
    pdf.pdf.text("Humpty Dumpty Fall Scale:", pdf.margin, pdf.currY);
    pdf.currY += 20;
    pdf.addTwoColumnText("Age", assessment.HDage,
      "Gender", assessment.HDgender);
    pdf.addOneColumnText("Diagnosis", assessment.HDdiagnosis);
    pdf.addOneColumnText("Cognitive Impairment", assessment.HDcognitiveImpairment);
    pdf.addOneColumnText("Environmental Factors", assessment.HDenvironmentalFactors);
    pdf.addOneColumnText("Response to Surgery", assessment.HDresponseToSurgery);
    pdf.addOneColumnText("Medication Usage", assessment.HDmedicationUsage);
    pdf.addOneColumnText("Total Humpty Dumpty Score", assessment.HDtotalScore.toString());
  } else {
    pdf.pdf.text("Fall Risk:", pdf.margin, pdf.currY);
    pdf.currY += 20;
    pdf.addTwoColumnText("Age", assessment.fallRiskAge,
      "Fall History", assessment.fallHistory ? "Yes" : "No");
    pdf.addOneColumnText("Elimination, Bowel, & Urine", assessment.fallRiskEbu);
    pdf.addOneColumnText("Medications", assessment.fallRiskMedications);
    pdf.addOneColumnText("Patient Care Equipment", assessment.fallRiskPce);
    pdf.addOneColumnText("Mobility", assessment.fallRiskMobility.join(", "));
    pdf.addOneColumnText("Cognition", assessment.fallRiskCognition.join(", "));
    pdf.addOneColumnText("Total Fall Risk Score", assessment.fallScore.toString());
    pdf.addOneColumnText("Fall Risk Status", assessment.fallScore >= 12 ? "At Risk" : "Not at Risk");
  }
  pdf.currY += 10;
  pdf.addOneColumnText("Limited Range of Motion", assessment.limitedRangeOfMotion);
  pdf.addOneColumnText("Deformities Present", assessment.deformitiesPresent);
  pdf.addOneColumnText("Other Findings", assessment.otherFindings);
}

function formToMusculoskeletalAssessment(fg: MusculoskeletalFormGroup) {
  return new MusculoskeletalAssessment({
    fallRiskAge: fg.controls.fallRiskAge.value!,
    fallHistory: fg.controls.fallHistory.value!,
    fallRiskEbu: fg.controls.fallRiskEbu.value!,
    fallRiskMedications: fg.controls.fallRiskMedications.value!,
    fallRiskPce: fg.controls.fallRiskPce.value!,
    fallRiskMobility: fg.controls.fallRiskMobility.value!,
    fallRiskCognition: fg.controls.fallRiskCognition.value!,
    fallScore: fg.controls.fallScore.value!,
    HDage: fg.controls.HDage.value!,
    HDgender: fg.controls.HDgender.value!,
    HDdiagnosis: fg.controls.HDdiagnosis.value!,
    HDcognitiveImpairment: fg.controls.HDcognitiveImpairment.value!,
    HDenvironmentalFactors: fg.controls.HDenvironmentalFactors.value!,
    HDresponseToSurgery: fg.controls.HDresponseToSurgery.value!,
    HDmedicationUsage: fg.controls.HDmedicationUsage.value!,
    HDtotalScore: fg.controls.HDtotalScore.value!,
    mobilityAndTransfers: fg.controls.mobilityAndTransfers.value!,
    assistiveDevices: fg.controls.assistiveDevices.value!,
    limitedRangeOfMotion: fg.controls.limitedRangeOfMotion.value!,
    deformitiesPresent: fg.controls.deformitiesPresent.value!,
    otherFindings: fg.controls.otherFindings.value!,
  });
}